import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL;
if (!baseURL) {
  throw new Error('REACT_APP_API_URL is not set. Add it to client/.env.development (or the relevant env file).');
}

// Default API configuration
const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include token in all requests
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      // Add token using standard Authorization Bearer header format
      config.headers['Authorization'] = `Bearer ${token}`;
      
      // Also include x-auth-token for backward compatibility
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      window.location.replace('/login');
    }
    return Promise.reject(error);
  }
);

// User API methods
export const registerUser = (userData) => api.post('/api/auth/register', userData);
export const loginUser = (userData) => api.post('/api/auth/login', userData);
export const getUser = () => api.get('/api/auth');

// Drawings API methods
export const uploadDrawing = (formData) => {
  return api.post('/api/drawings', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const getDrawings = () => api.get('/api/drawings');
export const getDrawing = (id) => api.get(`/api/drawings/${id}`);
export const updateDrawing = (id, data) => api.put(`/api/drawings/${id}`, data);
export const deleteDrawing = (id) => api.delete(`/api/drawings/${id}`);

// Drawing processing API methods
export const processDrawing = async (drawingId) => {
  console.log(`Starting processing for drawing ID: ${drawingId}`);
  if (!drawingId) {
    throw new Error('Drawing ID is required for processing');
  }
  const response = await api.post(`/api/drawings/${drawingId}/process`);
  return response.data;
};
export const getProcessingStatus = (id) => api.get(`/api/drawings/${id}/status`);

// Elements and connections API methods
export const getDrawingElements = (id) => api.get(`/api/drawings/${id}/elements`);
export const getDrawingConnections = (id) => api.get(`/api/drawings/${id}/connections`);
export const createElement = (elementData) => api.post('/api/elements', elementData);
export const updateElement = (id, data) => api.put(`/api/elements/${id}`, data);
export const createConnection = (connectionData) => api.post('/api/connections', connectionData);
export const updateConnection = (id, data) => api.put(`/api/connections/${id}`, data);
export const deleteConnection = (id) => api.delete(`/api/connections/${id}`);

// Export for CSV data
export const exportDrawingData = (id) => api.get(`/api/drawings/${id}/export`, { responseType: 'blob' });

// Checkpoint steering API methods
export const getCheckpointData = (drawingId) => api.get(`/api/drawings/${drawingId}/checkpoint`);
export const resumeCheckpoint = (drawingId, adjustments) => api.post(`/api/drawings/${drawingId}/checkpoint/resume`, adjustments);

// Support API methods
export const submitSupportRequest = (data) => api.post('/api/support', data);

export default api;